/**
 * Retries an idempotent operation in the given interval (i.e. does not wait for the first attempt to complete)
 * until an attempt succeeds or the maximum number of retries is reached.
 */
export function retryIdempotentOperation<T>(
  op: () => Promise<T>,
  options: { maxRetries: number; interval: number },
): Promise<T> {
  const { maxRetries, interval } = options;
  const maxAttempts = 1 + maxRetries;
  let numAttempts = 0;
  let numErrors = 0;
  let firstError: any;
  let isDone = false;
  return new Promise<T>((resolve, reject) => {
    // eslint-disable-next-line prefer-const
    let intervalId: ReturnType<typeof setInterval>;
    const attempt = async () => {
      if (isDone) {
        return;
      }
      if (++numAttempts >= maxAttempts) {
        clearInterval(intervalId);
      }
      try {
        const result = await op();
        if (!isDone) {
          isDone = true;
          clearInterval(intervalId);
          resolve(result);
        }
      } catch (error) {
        if (++numErrors === 1) {
          firstError = error;
        }
        if (!isDone && numAttempts >= maxAttempts && numErrors === numAttempts) {
          isDone = true;
          reject(firstError);
          clearInterval(intervalId);
        }
      }
    };
    intervalId = setInterval(attempt, interval);
    void attempt();
  });
}
